/** Website CMS building blocks shared by the page editor, media library and collections screens. */
import { createContext, useContext, useState, type ReactNode } from 'react';
import { Button, Text } from '@qm/ui';
import type { CmsMediaView } from '@qm/contracts/cms';
import type { Translate } from '@qm/i18n';
import { useSurface } from '../kit/context';
import { errorDetails, errorInfo } from '../kit/http';
import { errorMessage } from '../kit/ui';
import { mediaPreviewUrl } from '../lib/cms-logic';

/** Reasons the CMS and price-change API return in error details, plus the client-side upload/field checks. */
export const CMS_REASONS = [
  'stale_version', 'one_h1', 'unknown_media', 'unknown_collection', 'hand_owned', 'unsupported_type', 'too_large', 'type_mismatch', 'empty',
  'media_in_use', 'collection_not_empty', 'collection_in_use', 'category_not_empty', 'unknown_category', 'four_eyes', 'stripe_price_required',
  'stripe_price_mismatch', 'unknown_sku', 'contact_sales', 'currency_not_offered', 'invalid_key', 'duplicate_key', 'reserved_key', 'source_unavailable',
] as const;

/** Translated message for a CMS error: a known reason first, then the generic kit mapping. */
export function cmsErrorText(t: Translate, err: unknown): string {
  const info = errorInfo(err);
  const d = errorDetails(err);
  const obj = d && !Array.isArray(d) ? d : {};
  if (info.reason) {
    const k = `portal.admin.cms.err.${info.reason}`;
    const maxBytes = typeof obj.maxBytes === 'number' ? obj.maxBytes : 0;
    const msg = t(k, { found: String(obj.found ?? 0), id: String(obj.id ?? obj.key ?? ''), items: String(obj.items ?? ''), mb: String(Math.round(maxBytes / 1048576)) });
    if (msg !== k) return msg;
  }
  if (info.code === 'network' && err instanceof Error && err.message.startsWith('cms-source')) return t('portal.admin.cms.err.source_unavailable');
  return errorMessage(t, err);
}

/** Lines to list under the message: usedBy pages/items, Stripe mismatch problems, or zod field errors (path: message). */
export function cmsErrorList(err: unknown): string[] {
  const d = errorDetails(err);
  if (Array.isArray(d)) {
    return d.map((i) => {
      const issue = i as { path?: unknown; message?: unknown };
      const path = Array.isArray(issue.path) ? issue.path.join('.') : '';
      return `${path ? `${path}: ` : ''}${typeof issue.message === 'string' ? issue.message : ''}`.trim();
    }).filter(Boolean);
  }
  if (!d) return [];
  const list = Array.isArray(d.usedBy) ? d.usedBy : Array.isArray(d.problems) ? d.problems : [];
  return list.map(String);
}

export function CmsError({ error, onReload, testId }: { error: unknown; onReload?: () => void; testId?: string }) {
  const { t } = useSurface();
  if (!error) return null;
  const info = errorInfo(error);
  const list = cmsErrorList(error);
  return (
    <div className="qx-alert" role="alert" data-code={info.code} data-status={info.status} data-reason={info.reason ?? undefined} data-testid={testId ?? 'cms-error'}>
      <Text>{cmsErrorText(t, error)}</Text>
      {info.reason === 'media_in_use' || info.reason === 'collection_in_use' ? <Text>{t('portal.admin.cms.err.usedBy')}</Text> : null}
      {list.length ? <ul className="qx-list" data-testid="cms-error-list">{list.map((x, i) => <li key={i}>{x}</li>)}</ul> : null}
      {info.reason === 'stale_version' && onReload ? <Button onClick={onReload} data-testid="stale-reload">{t('portal.admin.cms.editor.reload')}</Button> : null}
      {info.correlationId ? <Text tone="muted">{t('portal.kit.reference', { id: info.correlationId })}</Text> : null}
    </div>
  );
}

/** A client-side check message (upload type/size, field key) with the same look as a server error. */
export function ClientError({ reason, params, testId }: { reason: string | null; params?: Record<string, string>; testId?: string }) {
  const { t } = useSurface();
  if (!reason) return null;
  return <div className="qx-alert" role="alert" data-reason={reason} data-testid={testId ?? 'client-error'}><Text>{t(`portal.admin.cms.err.${reason}`, { found: '', id: '', items: '', mb: '', ...params })}</Text></div>;
}

// ------------------------------------------------------------------ media library context + picker
export interface MediaLib { items: readonly CmsMediaView[]; origin: string; loading: boolean }
export const MediaLibContext = createContext<MediaLib>({ items: [], origin: '', loading: false });
export const useMediaLib = () => useContext(MediaLibContext);

export function MediaThumb({ media, id }: { media?: CmsMediaView | null; id: string; origin?: string }) {
  const { t, locale } = useSurface();
  if (!id) return null;
  if (media?.kind === 'video') return <span className="qx-media__thumb qx-media__thumb--video" data-kind="video" role="img" aria-label={t('portal.admin.cms.media.video')}><span aria-hidden="true">▶</span></span>;
  const alt = media ? (locale === 'ar' ? media.altAr : media.altEn) || media.filename : '';
  return <img className="qx-media__thumb" src={mediaPreviewUrl(id)} alt={alt} loading="lazy" data-kind="image" />;
}

/** Inline media chooser: a grid of the library (images only when onlyImages). Selected ids are marked. */
export function MediaPicker({ onPick, onlyImages, selected, onClose }: { onPick: (id: string) => void; onlyImages?: boolean; selected?: readonly string[]; onClose?: () => void }) {
  const { t } = useSurface();
  const lib = useMediaLib();
  const items = lib.items.filter((m) => !onlyImages || m.kind === 'image');
  return (
    <div className="qx-picker" role="group" aria-label={t('portal.admin.cms.media.pick')} data-testid="media-picker">
      {lib.loading ? <Text tone="muted">{t('portal.kit.loading')}</Text> : null}
      {!lib.loading && items.length === 0 ? <Text tone="muted">{t('portal.admin.cms.media.none')}</Text> : null}
      <div className="qx-media-grid">
        {items.map((m) => (
          <button key={m.id} type="button" className="qx-media qx-media--pick" data-media={m.id} aria-pressed={selected?.includes(m.id) ?? false} onClick={() => onPick(m.id)}>
            <MediaThumb media={m} id={m.id} origin={lib.origin} />
            <span className="qx-media__name">{m.filename}</span>
          </button>
        ))}
      </div>
      {onClose ? <Button onClick={onClose}>{t('portal.kit.close')}</Button> : null}
    </div>
  );
}

/** Two-step destructive action: the first click asks, the second confirms. */
export function ConfirmButton({ label, confirmLabel, question, onConfirm, disabled, testId, children }: {
  label: string; confirmLabel: string; question: string; onConfirm: () => void; disabled?: boolean; testId?: string; children?: ReactNode;
}) {
  const { t } = useSurface();
  const [asking, setAsking] = useState(false);
  if (!asking) return <Button onClick={() => setAsking(true)} disabled={disabled} data-danger="true" data-testid={testId}>{label}</Button>;
  return (
    <span className="qx-confirm" role="group" data-testid={testId ? `${testId}-confirm` : undefined}>
      <Text>{question}</Text>
      {children}
      <Button variant="primary" data-danger="true" onClick={() => { setAsking(false); onConfirm(); }} disabled={disabled}>{confirmLabel}</Button>
      <Button onClick={() => setAsking(false)}>{t('portal.admin.cms.cancel')}</Button>
    </span>
  );
}
